import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { CloseIcon } from './icons';
import { Button } from './Button';
import { cleanSubtitle } from './cleanSubtitle';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  footer?: ReactNode;
  /** ADR-0053: overrides the body wrapper's classes. A body that must give a DEFINITE
   *  height to a virtualised child (BrowseZoomModal's embedded AG-Grid) cannot use the
   *  default `overflow-auto` scroll box — the grid would collapse to 0 px inside it. */
  bodyClassName?: string;
  /** Help findings H-027/I-002 (ADR-0066): `data-help-id` on the dialog root. Omitted by default. */
  helpId?: string | undefined;
  children: ReactNode;
};

const SIZE_CLS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  /** ADR-0053: a whole PB child WINDOW (an embedded browse grid) is far wider than any
   *  form dialog — PB opens these as full MDI sheets / response windows, not narrow popups. */
  full: 'max-w-[96vw]',
} as const;

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  bodyClassName,
  helpId,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  const shownSubtitle = cleanSubtitle(subtitle);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      {/*
       * role/aria-modal/aria-labelledby added 2026-08-06. This dialog had no accessible role
       * at all, which is an a11y gap in its own right — but it also cost a whole functest
       * wave: `detectMode` (functest/tests/helpers.ts) recognises exactly two outcomes of a
       * New click, a URL navigation or an AG-Grid inline-edit row. A modal is neither, so on
       * every screen whose New opens one it returned 'none', `createRecord` re-clicked, and
       * the second click was swallowed by this backdrop until the 120s timeout — 25 identical
       * "lifecycle › create" failures with no product defect behind any of them.
       * `data-modal` is the stable hook the harness keys on; keep both.
       */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ph10-modal-title"
        data-modal="true"
        data-help-id={helpId}
        className={clsx(
          'relative flex max-h-[90vh] w-full flex-col rounded-lg bg-white shadow-xl',
          SIZE_CLS[size],
        )}
      >
        <header className="flex items-start justify-between border-b px-5 py-3">
          <div>
            <h2 id="ph10-modal-title" className="text-base font-semibold">
              {title}
            </h2>
            {shownSubtitle && <p className="mt-0.5 text-xs text-slate-500">{shownSubtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-m-1 rounded p-1 text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-phblue-400"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </header>
        <div className={bodyClassName ?? 'flex-1 overflow-auto px-5 py-4'}>{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t bg-slate-50 px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmModal({
  open,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  cancelIsDefault = false,
  helpId,
  confirmHelpId,
  cancelHelpId,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** Caption of the dismiss button (default "Cancel"; a PB yesno! box passes "No"). */
  cancelLabel?: string;
  danger?: boolean;
  /** slice-3956 (#3781): PB f_mmessage(…, YesNo!, 2) — DEFAULT BUTTON 2 (No). Focus Cancel on
   *  open so Enter/Space answers the safe choice (otherwise focus stays on the toolbar button
   *  that opened the dialog). Opt-in per call site. */
  cancelIsDefault?: boolean;
  /** Help finding I-002 (ADR-0066): `data-help-id` on the dialog root and its two buttons.
   *  All omitted by default. */
  helpId?: string | undefined;
  confirmHelpId?: string | undefined;
  cancelHelpId?: string | undefined;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      helpId={helpId}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel}
            autoFocus={cancelIsDefault}
            helpId={cancelHelpId}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            className={danger ? 'bg-red-600 hover:bg-red-700' : ''}
            helpId={confirmHelpId}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-slate-700">{message}</div>
    </Modal>
  );
}

/** Generic "not wired up yet / demo" modal that names the action. */
export function ActionPlaceholderModal({
  open,
  onClose,
  actionLabel,
  screenTitle,
  context,
}: {
  open: boolean;
  onClose: () => void;
  actionLabel: string;
  screenTitle: string;
  context?: ReactNode;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={actionLabel}
      subtitle={`${screenTitle} — action dispatcher`}
      size="md"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="space-y-3 text-sm">
        <p className="text-slate-700">
          This action is recognized by the action bar. In the migrated app it will open a focused
          dialog or flow scoped to the current record.
        </p>
        {context && (
          <div className="rounded border bg-slate-50 p-3 font-mono text-xs">{context}</div>
        )}
        <p className="text-xs text-slate-500">
          Wiring to the real handler (or the underlying stored procedure) is the next step.
        </p>
      </div>
    </Modal>
  );
}
